import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedPlural, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import {
  Card, CardBody, Col, Row,
} from 'reactstrap';

import Loading from 'src/components/Loading';
import {
  getTradeRulesAcceptedForProperty,
} from 'src/components/TradeRule/helpers';
import LinkInI18nBlock from 'src/enosikit/components/LinkInI18nBlock';
import { emptyTradeRulesClassName } from 'src/helpers/tradeHelpers';
import {
  CONTRACTED, TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL, TRADE_TYPE_CONTRACTED,
} from 'src/util/constants';

import TradeRule from './TradeRule';

const ContractedTradeRulesFragment = graphql`
  fragment ContractedTradeRules_property on Property {
    id
    meters {
      edges {
        node {
          id
          identifier
          title
          tradePointId
          contractedRules: rules(first: 500, type: TRADE_TYPE_CONTRACTED, state: TRADE_RULE_STATE_ACCEPTED) {
            edges {
              node {
                id
                priority
                tradeType
                state
                buyer {
                  userId
                  communityId
                  residualId
                  tradePoint {
                    id
                    meter {
                      id
                      identifier
                      title
                      property {
                        id
                        title
                      }
                    }
                  }
                }
                seller {
                  userId
                  communityId
                  residualId
                  tradePoint {
                    id
                    meter {
                      id
                      identifier
                      title
                      property {
                        id
                        title
                      }
                    }
                  }
                }
                clauses {
                  edges {
                    node {
                      price
                    }
                  }
                }
                start
                finish
                proposedAt
                proposedBy {
                  id
                  email
                  givenName
                  familyName
                }
                acceptedAt
                acceptedBy {
                  id
                  email
                  givenName
                  familyName
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * ContractedCard component displays contracted trade rules for a property.
 * @param {object} props - Component props
 * @param {object} props.property - Property containing meters and their associated
 * contracted trade rules
 * @returns {React.ReactElement} A Card component containing contracted trade information.
 */
function ContractedTradeRules(props) {
  if (!props || Object.keys(props).length === 0) {
    return <Loading />;
  }

  const intl = useIntl();

  let { property } = props;
  if (!property) {
    return <Loading />;
  }
  property = useFragment(ContractedTradeRulesFragment, property);

  const { id: propertyId } = property || {};
  const tradeRules = getTradeRulesAcceptedForProperty(property, TRADE_TYPE_CONTRACTED);
  const noTradeRulesClass = emptyTradeRulesClassName(tradeRules, CONTRACTED);
  const buyRulesCount = tradeRules?.buy?.length || 0;
  const sellRulesCount = tradeRules?.sell?.length || 0;

  // i18n variables
  const contractedTradesTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.contracted_trades_rules.title', defaultMessage: 'Contracted trades' });
  const buyingTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.contracted_trades_rules.buying.title', defaultMessage: 'Buying' });
  const sellingTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.contracted_trades_rules.selling.title', defaultMessage: 'Selling' });
  const singleTrade = intl.formatMessage({ id: 'trade_rule.trade_rule_active.contracted_trades_rules.trade.singular.label', defaultMessage: 'contracted trade rule' });
  const multipleTrade = intl.formatMessage({ id: 'trade_rule.trade_rule_active.contracted_trades_rules.trade.multiple.label', defaultMessage: 'contracted trade rules' });

  return (
    <Card className={`mt-4 mb-4 trade-rules-contracted${noTradeRulesClass}`}>
      <CardBody>
        <h2>{contractedTradesTitle}</h2>
        <p>
          <FormattedMessage
            id="trade_rule.trade_rule_active.contracted.help_text"
            defaultMessage="You have agreed to buy your energy from or sell your energy to one or more suppliers directly first as a contracted trade."
            values={{
              link: (chunks) => (
                <LinkInI18nBlock
                  url={intl.formatMessage({ id: 'trade_rule.trade_rule_active.contracted_trades_rules.help_text.url', defaultMessage: 'trade_rule.trade_rule_active.contracted_trades_rules.help_text.url' })}
                  urlKey="trade_rule.trade_rule_active.contracted_trades_rules.help_text.url"
                  chunks={chunks}
                />
              ),
            }}
          />
        </p>

        <Row>
          <Col xs="12" sm="12" md="6">
            <h3>{buyingTitle}</h3>
            <p>
              {intl.formatMessage(
                {
                  id: 'trade_rule.trade_rule_active.contracted_trades_rules.buy.count_details.label',
                  defaultMessage: 'You have {tradeRulesCount} {tradeRulesCountLabel} to buy energy.',
                },
                {
                  tradeRulesCount: <strong>{buyRulesCount}</strong>,
                  tradeRulesCountLabel: (
                    <FormattedPlural
                      value={buyRulesCount}
                      one={singleTrade}
                      other={multipleTrade}
                    />
                  ),
                },
              )}
            </p>

            {tradeRules?.buy?.map((rule) => (
              <TradeRule
                rule={rule}
                tradeDirection={TRADE_DIRECTION_BUY}
                tradeType={TRADE_TYPE_CONTRACTED}
                propertyId={propertyId}
                key={rule.id}
              />
            ))}
          </Col>
          <Col xs="12" sm="12" md="6">
            <h3>{sellingTitle}</h3>
            <p>
              {intl.formatMessage(
                {
                  id: 'trade_rule.trade_rule_active.contracted_trades_rules.sell.count_details',
                  defaultMessage: 'You have {tradeRulesCount} {tradeRulesCountLabel} to sell energy.',
                },
                {
                  tradeRulesCount: <strong>{sellRulesCount}</strong>,
                  tradeRulesCountLabel: <FormattedPlural
                    value={sellRulesCount}
                    one={singleTrade}
                    other={multipleTrade}
                  />,
                },
              )}
            </p>
            {tradeRules?.sell?.map((rule) => (
              <TradeRule
                rule={rule}
                tradeDirection={TRADE_DIRECTION_SELL}
                tradeType={TRADE_TYPE_CONTRACTED}
                propertyId={propertyId}
                key={rule.id}
              />
            ))}
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}

ContractedTradeRules.propTypes = {
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

ContractedTradeRules.defaultProps = {
  property: null,
};

export default ContractedTradeRules;
